import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './MonitoredIndividualCarousel.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import logComponent from '../../common/logComponent';
import MonitoredIndividualImage from '../MonitoredIndividualImage/MonitoredIndividualImage';

export default function MonitoredIndividualCarousel({ monitoredIndividuals, onChange, children }) {
  const [currentMonitoredIndividualIndex, setCurrentMonitoredIndividualIndex] = useState(0);
  const [firstMonitoredIndividualSelected, setFirstMonitoredIndividualSelected] = useState(true);
  const [lastMonitoredIndividualSelected, setLastMonitoredIndividualSelected] = useState(monitoredIndividuals.length === 1);
  const currentMonitoredIndividual = monitoredIndividuals[currentMonitoredIndividualIndex];

  if (logComponent('MonitoredIndividualCarousel')) { console.debug('MonitoredIndividualCarousel rendered'); }

  useEffect(() => {
    // pass current monitored individual back to parent
    if (logComponent('MonitoredIndividualCarousel')) { console.debug('MonitoredIndividualCarousel onMount'); }
    if (onChange) onChange(monitoredIndividuals[currentMonitoredIndividualIndex]);
  }, [])

  useEffect(() => {
    if (logComponent('MonitoredIndividualCarousel')) { console.debug('MonitoredIndividualCarousel useEffect [currentMonitoredIndividualIndex]'); }
    if (onChange) onChange(monitoredIndividuals[currentMonitoredIndividualIndex]);

    setFirstMonitoredIndividualSelected(currentMonitoredIndividualIndex === 0);
    setLastMonitoredIndividualSelected(currentMonitoredIndividualIndex === monitoredIndividuals.length - 1);

  }, [currentMonitoredIndividualIndex])

  useEffect(() => {
    if (logComponent('MonitoredIndividualCarousel')) { console.debug('MonitoredIndividualCarousel useEffect [monitoredIndividuals]'); }
    setCurrentMonitoredIndividualIndex(0);

  }, [monitoredIndividuals])

  const previousItem = () => {
    if (logComponent('MonitoredIndividualCarousel')) { console.debug('MonitoredIndividualCarousel previousItem'); }
    let newMonitoredIndividual;

    if (currentMonitoredIndividualIndex === 0) {
      newMonitoredIndividual = monitoredIndividuals.length - 1;
    } else {
      newMonitoredIndividual = currentMonitoredIndividualIndex - 1;
    }
    setCurrentMonitoredIndividualIndex(newMonitoredIndividual);

  }

  const nextItem = () => {
    if (logComponent('MonitoredIndividualCarousel')) { console.debug('MonitoredIndividualCarousel nextItem'); }
    let newMonitoredIndividualIndex;
    if (currentMonitoredIndividualIndex + 1 === monitoredIndividuals.length) {
      newMonitoredIndividualIndex = 0;
    } else {
      newMonitoredIndividualIndex = currentMonitoredIndividualIndex + 1;
    }
    setCurrentMonitoredIndividualIndex(newMonitoredIndividualIndex);

  }

  const setCurrentItem = (e) => {
    if (logComponent('MonitoredIndividualCarousel')) { console.debug('MonitoredIndividualCarousel setCurrentItem'); }
    setCurrentMonitoredIndividualIndex(+e.target.dataset.itemIndex);
  }

  return (
    <div className="Monitored-Individual-Carousel">
      {
        (monitoredIndividuals && monitoredIndividuals.length) > 0 &&
        <React.Fragment>
          <div className="Monitored-Individual-Carousel__main">
            <div className="Monitored-Individual-Carousel__image-row">
              <button
                aria-label="Previous Individual"
                onClick={previousItem}
                className={'Monitored-Individual-Carousel__control' + (firstMonitoredIndividualSelected ? ' Monitored-Individual-Carousel__control--disabled' : '')}
                disabled={firstMonitoredIndividualSelected}>
                <FontAwesomeIcon icon="chevron-left"></FontAwesomeIcon>
              </button>
              <div className="Monitored-Individual-Carousel__image">
                {monitoredIndividuals.map((monitoredIndividual, index) => {
                  return (monitoredIndividual.photoPath ?
                    <img
                      src={monitoredIndividual.photoPath}
                      alt={monitoredIndividual.monitoredIndividualId}
                      className={index !== currentMonitoredIndividualIndex ? 'hidden' : ''}
                      key={monitoredIndividual.monitoredIndividualId} /> :
                    <div
                      className={'Monitored-Individual-Carousel__monitoredindividual-image-placeholder' + (index !== currentMonitoredIndividualIndex ? ' hidden' : '')}
                      key={monitoredIndividual.monitoredIndividualId ? `mi-${monitoredIndividual.monitoredIndividualId}` : `unit-${monitoredIndividual.unitId}`} >
                      <MonitoredIndividualImage monitoredIndividual={monitoredIndividual } />
                    </div>)
                })
                }
              </div>
              <button
                aria-label="Next Individual"
                onClick={nextItem}
                className={'Monitored-Individual-Carousel__control' + (lastMonitoredIndividualSelected ? ' Monitored-Individual-Carousel__control--disabled' : '')}
                disabled={lastMonitoredIndividualSelected}>
                <FontAwesomeIcon icon="chevron-right"></FontAwesomeIcon>
              </button>
            </div>
            <div className="Monitored-Individual-Carousel__text-row">
              {
                currentMonitoredIndividual &&
                <React.Fragment>
                  {
                    currentMonitoredIndividual.monitoredIndividualId &&
                    <React.Fragment>
                      <span className="Monitored-Individual-Carousel__name">{`${currentMonitoredIndividual.legalFirstName} ${currentMonitoredIndividual.lastName}`}</span>
                      <span className="Monitored-Individual-Carousel__facility" title={currentMonitoredIndividual.lastKnownFacilityName}>{currentMonitoredIndividual.lastKnownFacilityName}</span>
                      <span className="Monitored-Individual-Carousel__room" title={currentMonitoredIndividual.lastAssignedUnitName}>{currentMonitoredIndividual.lastAssignedUnitName}</span>
                    </React.Fragment>
                  }
                  {
                    !currentMonitoredIndividual.monitoredIndividualId && currentMonitoredIndividual.unitId &&
                    <React.Fragment>
                      <span className="Monitored-Individual-Carousel__name">{currentMonitoredIndividual.name}</span>
                      <span className="Monitored-Individual-Carousel__facility">{currentMonitoredIndividual.facilityName}</span>
                    </React.Fragment>
                  }
                </React.Fragment>
              }
            </div>
          </div>
          {children}
          <div className="Monitored-Individual-Carousel__indicator-row">
            {monitoredIndividuals.map((monitoredIndividual, index) => {
              const ariaLabel = monitoredIndividual.monitoredIndividualId ? `${monitoredIndividual.legalFirstName} ${monitoredIndividual.lastName}` : monitoredIndividual.facilityName;
              return (
                <button
                  className={'Monitored-Individual-Carousel__indicator' + (index === currentMonitoredIndividualIndex ? ' Monitored-Individual-Carousel__indicator--active' : '')}
                  onClick={setCurrentItem}
                  key={monitoredIndividual.monitoredIndividualId ? monitoredIndividual.monitoredIndividualId : monitoredIndividual.unitId}
                  data-item-index={index}
                  aria-label={ariaLabel}></button>)
            })
            }
          </div>
        </React.Fragment>
      }
    </div>
  )
}

MonitoredIndividualCarousel.propTypes = {
  monitoredIndividuals: PropTypes.array.isRequired
};